/**
 * Fits the results stage into the center column: the video (with the map beside it when there is one) on top,
 * the timeline strip under both. The page is fixed-height, so the video is as wide as the height allows and the
 * slack goes to the strip, up to a share of the height; a tall column gives the video the whole width instead.
 * On a wide, short stage the height, not the width, limits the video, and the strip under it only takes more of
 * that height: there the strip moves beside the video, under the map, when the column beside the video is wide
 * enough for it, and the video gets the whole height. Either way the pair fills the stage: the map takes the
 * width the video leaves, and a full-height video is never capped at its share of the width (a cap would end the
 * video column above the map's).
 */

// the strip's share of the stage height: never smaller than the first, never larger than the second
export const STRIP_MIN_SHARE = 0.16;
export const STRIP_MAX_SHARE = 0.3;
// the map's share of the width when it sits beside the video
export const MAP_SHARE = 0.36;

/**
 * @param width stage width (px)
 * @param height stage height (px)
 * @param ratio video width / height
 * @param gap gap between the rows and the columns (px)
 * @param chrome height of the player's control bar below the picture (px)
 * @param side the map sits beside the video
 * @param stripFloor the least height the strip's panel can take (px), whatever the share says
 * @param stripShares [least, most] share of the height for the strip (a bigger video: a thin strip)
 * @param stripBesideMin the least width the strip needs to sit beside the video, under the map (0: never)
 * @returns null while the stage has no size yet, else { layout, videoWidth, mapWidth, rowHeight, mapHeight,
 *   stripHeight }: layout 'under' has the strip under the video and the map (rows rowHeight, stripHeight),
 *   layout 'beside' has the video spanning both rows and the map over the strip beside it (rows mapHeight,
 *   stripHeight). mapWidth is the map's width next to the video (0 without a map beside it): in both
 *   layouts all the width the video leaves, so the pair always fills the stage
 */
export const fitStage = ({
	width,
	height,
	ratio,
	gap,
	chrome,
	side,
	stripFloor = 0,
	stripShares = [STRIP_MIN_SHARE, STRIP_MAX_SHARE],
	stripBesideMin = 0,
}) => {
	if (!(width > 0) || !(height > 0) || !(ratio > 0)) return null;
	const stripMin = Math.max(stripFloor, Math.ceil(height * stripShares[0]));
	const stripMax = Math.max(stripMin, Math.floor(height * stripShares[1]));
	// whole pixels, rounded so the rows never add up to more than the stage
	const stageHeight = Math.floor(height);
	if (side && stripBesideMin > 0) {
		// with the strip beside it the picture can take the whole height: that video, not a share of the width,
		// decides whether the column left over can hold the strip (a video capped at its share would end above
		// the map, leaving a band under its control bar)
		const tallMax = Math.max(0, stageHeight - chrome - 1);
		const tallWidth = Math.floor(Math.max(0, tallMax * ratio));
		const sideWidth = Math.floor(width - gap - tallWidth);
		if (sideWidth >= stripBesideMin) {
			const stripHeight = stripMin;
			return {
				layout: 'beside',
				videoWidth: tallWidth,
				mapWidth: sideWidth,
				rowHeight: Math.ceil(tallWidth / ratio + chrome),
				mapHeight: Math.max(0, stageHeight - gap - stripHeight),
				stripHeight,
			};
		}
	}
	// the width limited the video, or the column beside a full-height one is too narrow for the strip: the strip
	// goes under both, and the video is sized for the height that leaves it. The map takes all the width beside
	// it (its share, when the width limited the video; more when the height did), so the pair fills the stage
	// rather than sitting centred with a band either side
	const pictureMax = Math.max(0, stageHeight - gap - stripMin - chrome - 1);
	const widthMax = side ? (width - gap) * (1 - MAP_SHARE) : width;
	const videoWidth = Math.floor(Math.max(0, Math.min(widthMax, pictureMax * ratio)));
	const mapWidth = side ? Math.max(0, Math.floor(width - gap - videoWidth)) : 0;
	const rowHeight = Math.ceil(videoWidth / ratio + chrome);
	const stripHeight = Math.min(stripMax, Math.max(stripMin, stageHeight - gap - rowHeight));
	return { layout: 'under', videoWidth, mapWidth, rowHeight, mapHeight: rowHeight, stripHeight };
};
